/**
 * Shared Google Fit sync helpers.
 *
 * These wrap the backend fitness routes so any page that needs step-sync
 * capability (StepTrackingPage, LogActivityPage, and any future caller)
 * uses the same code path. Do not re-implement the endpoints inline —
 * extend these helpers instead. Same pattern as MetricInput /
 * parseMetricValue / submitImpactEntry in ChallengesPages.js.
 *
 * None of these throw. On any failure they return a `{ok: false, ...}`
 * shape so callers can render a friendly toast without duplicating
 * error-normalisation.
 */
import axios from './api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Trigger a fresh Google Fit sync. On success the backend writes
 * one `step_data` row per calendar day for the last window with
 * `source: "google_fit"`.
 *
 * Returns:
 *   { ok: true,  steps: <int> }                   — successful sync
 *   { ok: false, needsConnect: true }             — user hasn't linked Google Fit yet
 *   { ok: false, error: "<message>" }             — network / server error
 */
export const syncGoogleFitSteps = async () => {
  try {
    const res = await axios.post(`${API}/fitness/google/sync`, {}, { headers: authHeaders() });
    const steps = typeof res?.data?.steps === 'number' ? res.data.steps : 0;
    return { ok: true, steps };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    // Backend returns 400 when Google Fit is not connected (or the token
    // has expired without a refresh); expose that separately so callers
    // can render a "Connect Google Fit" CTA instead of a generic error.
    const notConnected =
      status === 400 &&
      typeof detail === 'string' &&
      /not connected|not configured/i.test(detail);
    if (notConnected) return { ok: false, needsConnect: true };
    return {
      ok: false,
      error: (typeof detail === 'string' ? detail : null) || 'Sync failed. Please try again.',
    };
  }
};

/**
 * Read connection status for Google Fit.
 * Returns { ok, connected, configured } — connected implies the user has
 * linked their account; configured implies OAuth is set up server-side.
 */
export const getFitStatus = async () => {
  try {
    const res = await axios.get(`${API}/fitness/google/status`, { headers: authHeaders() });
    return { ok: true, connected: !!res?.data?.connected, configured: !!res?.data?.configured };
  } catch {
    return { ok: false, connected: false, configured: false };
  }
};

/**
 * Read today's step summary. This endpoint has a side effect on the
 * backend — it backfills the last 7 days on every call — same behaviour
 * StepTrackingPage relies on today.
 *
 * Returns:
 *   { ok: true, steps, source, connected, goal, progress_percentage }
 *   { ok: false }
 *
 * `source` may be "google_fit", "manual", or null (when there is no
 * `step_data` doc for today).
 */
export const getTodaySteps = async () => {
  try {
    const res = await axios.get(`${API}/fitness/steps/today`, { headers: authHeaders() });
    const d = res?.data || {};
    return {
      ok: true,
      steps: typeof d.steps === 'number' ? d.steps : 0,
      source: d.source || null,
      connected: !!d.connected,
      goal: typeof d.goal === 'number' ? d.goal : 10000,
      progress_percentage: typeof d.progress_percentage === 'number' ? d.progress_percentage : 0,
    };
  } catch {
    return { ok: false, steps: 0, source: null, connected: false };
  }
};
